import React, { useState } from 'react';
import { ChevronLeft, ChevronRight, Quote, Star } from 'lucide-react';
import { testimonials } from '../data/mock';
import { Button } from './ui/button';

const Testimonials = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [expandedIndex, setExpandedIndex] = useState(null);

  const nextTestimonial = () => {
    setActiveIndex((prev) => (prev + 1) % testimonials.length);
    setExpandedIndex(null);
  };

  const prevTestimonial = () => {
    setActiveIndex((prev) => (prev - 1 + testimonials.length) % testimonials.length);
    setExpandedIndex(null);
  };

  const truncateText = (text, maxLength = 350) => {
    if (text.length <= maxLength) {
      return text;
    }
    const truncated = text.substring(0, maxLength);
    const lastSpace = truncated.lastIndexOf(' ');
    return lastSpace > 0 ? truncated.substring(0, lastSpace) + '...' : truncated + '...';
  };

  const isExpanded = expandedIndex === activeIndex;
  const displayText = isExpanded 
    ? testimonials[activeIndex].content 
    : truncateText(testimonials[activeIndex].content);

  return (
    <section id="testimonials" className="py-24 bg-slate-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <span className="inline-block text-blue-600 font-semibold text-sm uppercase tracking-wider mb-4">
            Testimonials
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 mb-6">
            What Our{' '}
            <span className="text-green-500">Clients Say</span>
          </h2>
          <p className="text-lg text-slate-600">
            Don&apos;t just take our word for it. Here&apos;s what our clients have to say about working with us.
          </p>
        </div>

        {/* Testimonials Carousel */}
        <div className="relative">
          {/* Main Testimonial */}
          <div className="max-w-4xl mx-auto">
            <div className="bg-white rounded-3xl shadow-xl p-8 md:p-12 relative min-h-[420px] sm:min-h-[360px]">
              {/* Quote Icon */}
              <div className="absolute -top-6 left-12">
                <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center">
                  <Quote className="w-6 h-6 text-white" />
                </div>
              </div>

              {/* Stars */}
              <div className="flex gap-1 mb-6">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-5 h-5 fill-amber-400 text-amber-400" />
                ))}
              </div>

              {/* Content */}
              <blockquote className="text-xl md:text-2xl text-slate-700 leading-relaxed mb-4 min-h-[200px] sm:min-h-[180px]">
                "{displayText}"
              </blockquote>

              {/* Read More / Read Less */}
              {truncateText(testimonials[activeIndex].content) !== testimonials[activeIndex].content && (
                <button
                  onClick={() => setExpandedIndex(isExpanded ? null : activeIndex)}
                  className="text-blue-600 hover:text-blue-700 font-semibold text-sm mb-8 transition-colors"
                >
                  {isExpanded ? 'Read Less' : 'Read More'}
                </button>
              )}

              {/* Author */}
              <div>
                <div className="font-bold text-slate-900">
                  {testimonials[activeIndex].name}
                </div>
                <div className="text-slate-500">
                  {testimonials[activeIndex].role}
                </div>
              </div>
            </div>
          </div>

          {/* Navigation Buttons */}
          <div className="flex items-center justify-center gap-3 mt-8 px-2">
            <Button
              variant="outline"
              size="icon"
              onClick={prevTestimonial}
              className="w-12 h-12 rounded-full border-slate-200 hover:border-blue-500 hover:text-blue-500 shrink-0"
            >
              <ChevronLeft className="w-5 h-5" />
            </Button>
            
            {/* Dots */}
            <div className="flex items-center gap-2 overflow-x-auto max-w-[60vw] sm:max-w-none min-w-0 px-1">
              {testimonials.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setActiveIndex(index)}
                  className={`w-2.5 h-2.5 rounded-full transition-all duration-300 ${
                    index === activeIndex
                      ? 'bg-blue-600 w-8'
                      : 'bg-slate-300 hover:bg-slate-400'
                  }`}
                />
              ))}
            </div>

            <Button
              variant="outline"
              size="icon"
              onClick={nextTestimonial}
              className="w-12 h-12 rounded-full border-slate-200 hover:border-blue-500 hover:text-blue-500 shrink-0"
            >
              <ChevronRight className="w-5 h-5" />
            </Button>
          </div>
        </div>

        {/* Client Logos */}
        <div className="mt-20">
          <div className="flex justify-center">
            <div className="inline-flex items-center gap-2 bg-white border border-slate-200 rounded-full px-4 py-2 shadow-sm">
              <span className="text-sm sm:text-base font-semibold text-slate-600 tracking-wide">
                Trusted by leading organizations worldwide
              </span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Testimonials;
